import React, { useEffect, useState } from 'react';
import api from '@/lib/api';
import { Bell, X, CheckCircle2, Loader2, Mail } from 'lucide-react';

/**
 * WaitlistBell — animated bell + modal to capture emails for a coming-soon course
 * or for the community. Shows "X people waiting" count.
 *
 * Props:
 *   - type: 'course' | 'community'
 *   - courseSlug: string (required if type='course')
 *   - courseTitle: string (display name for confirmation)
 *   - count: number (optional pre-filled count, otherwise fetched)
 *   - variant: 'compact' | 'full' (full shows count + label, compact is icon-only)
 */
export default function WaitlistBell({ type = 'course', courseSlug = '', courseTitle = '', count: initCount, variant = 'compact', className = '' }) {
  const [open, setOpen] = useState(false);
  const [email, setEmail] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [err, setErr] = useState('');
  const [count, setCount] = useState(initCount ?? null);

  useEffect(() => {
    if (initCount != null) { setCount(initCount); return; }
    api.get('/waitlist/counts').then(r => {
      const d = r.data || {};
      if (type === 'community') setCount(d.community || 0);
      else if (courseSlug) setCount((d.courses || {})[courseSlug] || 0);
    }).catch(() => {});
  }, [type, courseSlug, initCount]);

  const submit = async (e) => {
    e?.preventDefault?.();
    setErr('');
    if (!email || !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email.trim())) { setErr('Please enter a valid email.'); return; }
    setSubmitting(true);
    try {
      await api.post('/waitlist', { email: email.trim(), type, course_slug: courseSlug || undefined, course_title: courseTitle || undefined });
      setSubmitted(true);
      setCount((c) => (c ?? 0) + 1);
    } catch (e2) {
      setErr(e2?.response?.data?.detail || 'Something went wrong. Try again.');
    } finally { setSubmitting(false); }
  };

  return (
    <>
      <button
        onClick={(e) => { e.preventDefault(); e.stopPropagation(); setOpen(true); }}
        className={`group relative inline-flex items-center gap-2 ${className}`}
        aria-label="Join waitlist"
        data-testid={`waitlist-bell-${type}${courseSlug ? '-' + courseSlug : ''}`}
      >
        <span className="relative inline-flex items-center justify-center w-10 h-10 rounded-full bg-[color:var(--kc-gold)] text-black shadow-[0_8px_24px_-8px_rgba(212,164,55,0.7)] group-hover:scale-110 transition-transform">
          <Bell size={16} className="kc-bell-ring" />
          <span className="absolute inset-0 rounded-full ring-2 ring-[color:var(--kc-gold)] opacity-60 animate-ping pointer-events-none" />
        </span>
        {variant === 'full' && (
          <span className="text-left">
            <span className="block text-[10px] font-bold uppercase tracking-[0.18em] text-[color:var(--kc-gold)]">Notify Me</span>
            <span className="block text-[10px] text-white/70">{count != null ? `${count} ${count === 1 ? 'person' : 'people'} waiting` : 'Join the waitlist'}</span>
          </span>
        )}
      </button>

      {open && (
        <div className="fixed inset-0 z-[200] flex items-end md:items-center justify-center p-0 md:p-4" data-testid="waitlist-modal">
          <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={() => setOpen(false)} />
          <div className="relative w-full md:max-w-md bg-ink text-white rounded-t-2xl md:rounded-[var(--kc-radius-md)] shadow-2xl overflow-hidden kc-fade-up">
            <button onClick={() => setOpen(false)} className="absolute top-3 right-3 w-9 h-9 inline-flex items-center justify-center rounded-full hover:bg-white/10 transition-colors" aria-label="Close" data-testid="waitlist-close"><X size={18} /></button>
            {!submitted ? (
              <form onSubmit={submit} className="p-6 md:p-8">
                <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-[color:var(--kc-gold)] text-black mb-4">
                  <Bell size={22} />
                </div>
                <div className="text-[11px] tracking-[0.22em] uppercase text-[color:var(--kc-gold)]" data-testid="waitlist-eyebrow">
                  {type === 'community' ? 'Community Waitlist' : 'Course Waitlist'}
                </div>
                <h3 className="mt-2 font-display uppercase text-3xl leading-tight" data-testid="waitlist-title">
                  {type === 'community' ? 'Be the first one in.' : `Get notified when ${courseTitle || 'this course'} drops.`}
                </h3>
                <p className="mt-3 text-white/70 text-sm leading-relaxed">
                  {type === 'community'
                    ? 'No spam. Just one email when the doors open.'
                    : 'One email. The moment the lessons go live. That\u2019s it.'}
                </p>
                {count != null && count > 0 && (
                  <div className="mt-4 inline-flex items-center gap-2 text-xs text-white/60">
                    <span className="inline-block w-1.5 h-1.5 rounded-full bg-[color:var(--kc-gold)] animate-pulse" />
                    {count} {count === 1 ? 'person' : 'people'} already waiting
                  </div>
                )}
                <div className="mt-5 relative">
                  <Mail size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/40" />
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="you@email.com"
                    className="w-full h-12 pl-10 pr-3 rounded-[var(--kc-radius-sm)] bg-white/5 border border-white/15 text-white placeholder-white/40 focus:outline-none focus:ring-2 focus:ring-[color:var(--kc-gold)] focus:border-transparent"
                    autoFocus
                    required
                    data-testid="waitlist-email-input"
                  />
                </div>
                {err && <div className="mt-2 text-sm text-[color:var(--kc-danger)]" data-testid="waitlist-error">{err}</div>}
                <button type="submit" disabled={submitting} className="mt-4 w-full h-12 inline-flex items-center justify-center gap-2 rounded-[var(--kc-radius-sm)] bg-[color:var(--kc-gold)] text-black font-semibold hover:bg-[color:var(--kc-gold-2)] disabled:opacity-60 transition-colors" data-testid="waitlist-submit">
                  {submitting ? <><Loader2 size={16} className="animate-spin" /> Joining…</> : <>Notify me <Bell size={14} /></>}
                </button>
                <p className="mt-3 text-[10px] text-white/40 text-center">We’ll never share your email.</p>
              </form>
            ) : (
              <div className="p-6 md:p-8 text-center" data-testid="waitlist-success">
                <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-[color:var(--kc-gold)] text-black mb-4 kc-fade-up">
                  <CheckCircle2 size={28} />
                </div>
                <h3 className="font-display uppercase text-3xl">You’re on the list.</h3>
                <p className="mt-3 text-white/70">We’ll send you one email the moment it goes live. No spam, no nonsense.</p>
                <button onClick={() => setOpen(false)} className="mt-6 h-11 px-5 rounded-[var(--kc-radius-sm)] border border-white/20 hover:bg-white/10 transition-colors font-semibold" data-testid="waitlist-success-close">Got it</button>
              </div>
            )}
          </div>
        </div>
      )}

      <style>{`
        @keyframes kcBellRing { 0%, 100% { transform: rotate(0); } 15% { transform: rotate(-12deg); } 30% { transform: rotate(10deg); } 45% { transform: rotate(-8deg); } 60% { transform: rotate(6deg); } 75% { transform: rotate(-3deg); } }
        .kc-bell-ring { transform-origin: 50% 0; animation: kcBellRing 2.4s ease-in-out infinite; }
        .group:hover .kc-bell-ring { animation-duration: 1s; }
      `}</style>
    </>
  );
}
